import {RemoteConstantsModel} from "../models/remoteConstants.model";

// "Used" here means "referenced from RemoteConstants data" — entity ids,
// Metadata uids/ids, image/sound/imagePack keys, etc. Keys referenced only
// from Client (C#) code aren't tracked here; they show as unused (blue) in
// the editor, which is honest from the data's point of view. The
// `Rift/Documentation/audits/analyze-loc-usage.py` script is the source of
// truth for the broader "in use anywhere" check.
export function getUsedLocalizationKeys(state: RemoteConstantsModel): Set<string> {
    const keys = new Set<string>();

    // Special constants
    if (state.startingUnit) keys.add(state.startingUnit);
    if (state.startingBuilding) keys.add(state.startingBuilding);
    if (state.barracksId) keys.add(state.barracksId);

    // Units
    if (state.units) {
        for (const unit of state.units) {
            keys.add(unit.id);
            if (unit.attributes) {
                for (const attr of unit.attributes) keys.add(attr);
            }
        }
    }

    // Buildings
    if (state.buildings) {
        for (const building of state.buildings) {
            keys.add(building.id);
            if (building.attributes) {
                for (const attr of building.attributes) keys.add(attr);
            }
            if (building.produceableUnits) {
                for (const uid of building.produceableUnits) keys.add(uid);
            }
            if (building.rewards) {
                for (const level of building.rewards) {
                    if (level) {
                        for (const reward of level) keys.add(reward);
                    }
                }
            }
        }
    }

    // Tiles
    if (state.tiles) {
        for (const tile of state.tiles) {
            keys.add(tile.id);
            if (tile.supportingTile) keys.add(tile.supportingTile);
            if (tile.tileType) keys.add(tile.tileType);
            if (tile.resources) {
                for (const r of tile.resources) keys.add(r);
            }
            if (tile.buildings) {
                for (const b of tile.buildings) keys.add(b);
            }
        }
    }

    // Resources
    if (state.resources) {
        for (const resource of state.resources) {
            keys.add(resource.id);
            if (resource.attributes) {
                for (const attr of resource.attributes) keys.add(attr);
            }
        }
    }

    // Tech tree
    if (state.techTree) {
        for (const node of state.techTree) {
            keys.add(node.id);
            keys.add(`${node.id}.description`);
            if (node.unlocks) {
                for (const unlock of node.unlocks) keys.add(unlock);
            }
            if (node.children) {
                for (const child of node.children) keys.add(child);
            }
        }
    }

    // All metadata collections: uid as instance name, id.description as shared description
    const metadataCollections = [
        state.unitAttributes,
        state.buildingAttributes,
        state.techActionAttributes,
        state.techPassiveAttributes,
        state.rewards,
    ];
    const descriptionIds = new Set<string>();
    for (const collection of metadataCollections) {
        if (!collection) continue;
        for (const meta of collection) {
            keys.add(meta.uid);
            if (meta.id) descriptionIds.add(meta.id);
        }
    }
    descriptionIds.forEach(id => keys.add(`${id}.description`));

    // Asset keys
    if (state.images) {
        for (const key of Object.keys(state.images)) keys.add(key);
    }
    if (state.sounds) {
        for (const key of Object.keys(state.sounds)) keys.add(key);
    }
    if (state.imagePacks) {
        for (const key of Object.keys(state.imagePacks)) keys.add(key);
    }

    return keys;
}
